import { debounce } from 'lodash';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BiSolidPlaneAlt } from 'react-icons/bi';
import { HiOutlineBanknotes } from 'react-icons/hi2';
import { useRecoilState, useRecoilValue } from 'recoil';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const PaginatedBookSlider = dynamic(
  () => import('../../../components/BookComp/PaginatedBookSlider'),
);
// const PaginatedCoursesSlider = dynamic(
//   () => import('../components/Courses/PaginatedCoursesSlider'),
// );
// const PaginatedSchoolSlider = dynamic(
//   () => import('../components/ResourcesPage/SchoolsPage/PaginatedSchoolSlider'),
// );

// const HeroSection = dynamic(
//   () => import('../components/V2/HomePage/HeroSection'),
// );
// const HomepageSlider = dynamic(
//   () => import('../components/V2/HomePage/HomepageSlider'),
// );

import Slider from '@ant-design/react-slick';
import { BadgeCheckIcon, MinusIcon, PlusIcon } from '@heroicons/react/solid';
import { CollapseProps } from 'antd';
import {
  author1,
  author2,
  author3,
  author4,
  company1,
  company2,
  company3,
  company4,
  company5,
  internetImage,
  percentageHand,
} from 'assets';
import BookEarthIcon from 'assets/Icons/BookEarthIcon';
import LogoSvg from 'assets/Icons/orangeLogo';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import Image from 'next/image';

import CallBackForm from '@/components/HomePage/CallBackForm';
import CallBackFormModal from '@/components/HomePage/CallBackForm/modal';
import CallBackSuccessModal from '@/components/HomePage/CallBackForm/modal/success';
import RequestCallBack from '@/components/HomePage/RequestCallBack';
import SubTitle from '@/components/Pricing/SubTitle';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';

import { tokenUpdate } from '../../../atoms/authModalControl';
import { user } from '../../../atoms/user';
import { formateErrorObject } from '../../../components/shared/formateErrorObject';
import { getLoginToken } from '../../../components/shared/getToken';
import { notify } from '../../../components/shared/notify';
import { EventDetails, logEvent } from '../../../hooks/useClickEventTracking';
import { Endpoints } from '../../../network';
import {
  getAllBookCategories,
  getAllOnBoardingCategories,
  getAllShortBookListApi,
} from '../../../services/Library/Books';
import { getAllProducts } from '../../../services/Products';
import { BundleData } from '../../../utils/interfaces/Products/bundles';
import { User } from '../../../utils/interfaces/User';

export const generateUrlForImageBanner = (
  baseUrl: string,
  page: string,
  type: string,
): string => {
  const url = `${baseUrl}api/banner/images/${page}${type}`;
  return url;
};

const Home: React.FC = () => {
  const [, setShortBookLoading] = useState(false);
  const router = useRouter();
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const keyWord = router.query.search;
  const [, setBundleLoading] = useState(false);
  const [, setSearch] = useState<string>();
  const userData: User | undefined = useRecoilValue(user);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const sliderRef = useRef<Slider | null>(null);
  // const sliderTestimonialsRef = useRef<Slider | null>(null);

  const [, setToken] = useState('');
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [, setBundleData] = useState<BundleData[]>([]);
  const [, setCategories] = useState<string>();
  const [, setShortBooks] = useState<
    {
      _id: string;
      coverImageUrl: string;
      title: string;
      externalLink: string;
      slug: string;
      type: string;
      isListed: boolean;
      totalPages: number;
      isExternalLink: boolean;
    }[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
      if (token) {
        handleGetCategories();
      }
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };


  const generateUrlForImage = (
    baseUrl: string,
    endpoint: string,
    id: string,
  ): string => {
    const encodedId = encodeURIComponent(id);
    const url = `${baseUrl}${endpoint}${encodedId}`;
    return url;
  };

  const handleGetCategories = async () => {
    try {
      const response = await getAllBookCategories();

      const allOnBoardingCategories = await getAllOnBoardingCategories('4');
      const { data } = response;

      setCategories(
        (data ?? response)
          .filter((category: any) => category.bookCount >= 1)
          .filter((category: any) =>
            allOnBoardingCategories.categories.includes(category?.name),
          )
          .map((category: any) => category?._id)
          .join(','),
      );
    } catch (error: any) {
      notify(formateErrorObject(error.message), 'error');
    }
  };

  const handleBundleDetails = async (title?: string) => {
    try {
      setBundleLoading(true);

      const data = await getAllProducts(1, 7, undefined, title, 20);

      const formattedData = formatBundleData(data?.data);
      if (formattedData.length === 0) {
        setBundleData([]);
        return;
      }

      setBundleData((prevData) => {
        const uniqueData = prevData?.filter(
          (prevItem) =>
            !formattedData.some(
              (newItem) => newItem.hardCopyId === prevItem.hardCopyId,
            ),
        );
        return [...uniqueData, ...formattedData];
      });
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setBundleLoading(false);
    }
  };

  const formatBundleData = (rawData: any[]): BundleData[] => {
    return rawData.map((bundle) => ({
      image: bundle?.thumbnail_image || '',
      softCopyId: bundle?.digital_pair?.id || '',
      hardCopyId: bundle?.id || '',
      heading: bundle?.name || '',
      slug: bundle?.slug || '',
      softCopyHeading: bundle?.digital_pair?.name || 0,
      desc: bundle?.description || '',
      currency: bundle?.currency_symbol || '',
      actualPriceHardCopy: bundle?.stroked_price || 0,
      actualPriceSoftCopy:
        (bundle?.digital_pair && bundle.digital_pair?.stroked_price) || 0,
      hardCopyPrice: bundle?.main_price || 0,
      softCopyPrice:
        (bundle?.digital_pair && bundle?.digital_pair.main_price) || 0,
      type: bundle?.category || '',
      external_link: bundle?.external_link || '',
      products: bundle?.bundle_products,
    }));
  };

  const handleGetAllShortBooks = async (title?: string) => {
    try {
      setShortBookLoading(true);
      const response = await getAllShortBookListApi(title);
      const { data } = response;
      const formattedBooks = data
        ?.map((book: any) => ({
          _id: book?._id,
          coverImageUrl: generateUrlForImage(
            `${process.env.NEXT_PUBLIC_API_READER_URL}/`,
            `${Endpoints.getCoverImagesForBooks}/`,
            `${book?._id}`,
          ),
          title: book?.title,
          slug: book?.slug,
          type: book?.type,
          totalPages: book?.totalPages,
          isListed: book.isListed,
          isExternalLink: book?.isExternalLink,
        }))
        .filter(
          (book: any) => book.type === 'short' && book.isListed !== false,
        );

      setShortBooks(formattedBooks);
    } catch (error: any) {
      notify(error, 'error');
    } finally {
      setShortBookLoading(false);
    }
  };

  const handleSearch = async (keyword: string) => {
    if (keyword !== undefined) {
      setSearch(keyword);
      handleGetAllShortBooks(keyword);
      handleBundleDetails(keyword);
    } else {
      handleGetAllShortBooks();
      handleBundleDetails();
    }
  };

  const delayedSearch = debounce((keyword: string) => {
    handleSearch(keyword);
    if (keyWord) {
      const eventParams: EventDetails = {
        event_name: 'search_keyword',
        source: 'global',
        source_type: 'search',
        source_id: null,
        sub_source: null,
        sub_source_id: null,
        unit: 'string',
        value: keyword,
      };

      logEvent({
        eventDetails: eventParams,
        userData,
        pathname: router.pathname,
      });
    }
  }, 500);
  const isStaging = process.env.NEXT_PUBLIC_APP_ENV === 'staging';

  const categoryIdFilter = isStaging
    ? '66d95bde2da439ae0a02b8b0'
    : '66c6db9e104ce84c7e2a8956';

  const nowMinus6Months = moment().subtract(6, 'months').format('YYYY-MM-DD');

  useEffect(() => {
    delayedSearch(searchKeyword as string);
    return () => {
      delayedSearch.cancel();
      setSearch(undefined);
    };
  }, [searchKeyword]);

  useEffect(() => {
    handleGetAllShortBooks();
    handleBundleDetails();
    fetchData();

    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);



  useEffect(() => {
    setSearchKeyword(keyWord as string);
  }, [keyWord]);

  // const countBooksAndCourses = (products: any[]) => {
  //   let bookCount = 0;
  //   let courseCount = 0;

  //   products?.forEach((product: any) => {
  //     if (product?.category === 'Book') {
  //       bookCount++;
  //     } else if (product.category === 'Recorded Course') {
  //       courseCount++;
  //     }
  //   });

  //   return {
  //     bookCount,
  //     courseCount,
  //   };
  // };
  const headerClass = 'text-white md:text-5xl text-2xl';

  const data = [
    {
      other: 'Mostly Offline Sales – Not accessible to Self Published Books',
      orange: 'One of the highest selling platforms in the country.',
    },
    {
      other: 'No Training Provided',
      orange:
        'Book Accelerator Program – to make you self-capable to Sell the book',
    },
    {
      other: 'Black and White',
      orange: 'Black And White Or Illustrative',
    },
    {
      other: 'Does not understand brands/ business.',
      orange: 'Specialises in working with Brands/ Businesses',
    },
    {
      other: 'Cheaper',
      orange: 'Relatively Expensive',
    },
    {
      other: 'Lower Quality',
      orange: 'Higher Quality',
    },
    {
      other: 'No Speech to Book Tools Available',
      orange:
        'Speech to Book + Writing Quality AI tools to ensure highest quality of writing',
    },
  ];

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'How is Oranges different from other self-publishing platforms?',
      children: (
        <p className='text-grayZl-500'>
          Unlike most self-publishing companies that only print your book,
          Oranges by Zebralearn trains you to become a{' '}
          <span className='font-medium'>Book-Preneur</span>. We provide premium
          publishing, AI-powered writing tools, and our exclusive Book
          Accelerator Program to help you market and grow your book as a
          business asset.
        </p>
      ),
    },
    {
      key: '2',
      label: 'What formats can I publish in?',
      children: (
        <ul className='list-disc pl-5 text-grayZl-500 space-y-1'>
          <li>Paperback</li>
          <li>Hardcover</li>
          <li>E-book / Kindle</li>
          <li>Audiobook</li>
        </ul>
      ),
    },
    {
      key: '3',
      label: 'Do I retain full rights and copyright to my book?',
      children: (
        <p className='text-grayZl-500'>
          Yes. <span className='font-medium'>100%</span>. Your book is your
          intellectual property, and you keep complete ownership of your content
          and copyright. Oranges only provides services to publish, distribute,
          and empower you to sell effectively.
        </p>
      ),
    },
    {
      key: '4',
      label: 'Do you take responsibility for sales and marketing?',
      children: (
        <p className='text-grayZl-500'>
          No, we don’t directly sell or market your book. However, we make you,
          the author, fully capable of driving sales through our{' '}
          <span className='font-medium'>Book Accelerator Program</span>, which
          equips you with strategies, tools, and skills to generate sales and
          build your brand.
        </p>
      ),
    },
    {
      key: '5',
      label: 'What is the Book Accelerator Program?',
      children: (
        <div className='space-y-2 text-grayZl-500'>
          <p>
            It’s a structured 6-week training program designed to make authors
            self-reliant in selling their books. You’ll learn how to:
          </p>
          <ul className='list-disc pl-5 space-y-1'>
            <li>Build your author brand</li>
            <li>Market your book effectively</li>
            <li>Drive sales through multiple channels</li>
            <li>Leverage your book for business opportunities</li>
          </ul>
        </div>
      ),
    },
    {
      key: '6',
      label: 'How much does publishing cost?',
      children: (
        <p className='text-grayZl-500'>
          We offer multiple packages depending on the level of publishing and
          services you choose. You can check the Pricing table for all plans.
          All packages include editing, formatting, cover design, and publishing
          in your chosen formats.
        </p>
      ),
    },
    {
      key: '7',
      label: 'Do you provide editing and design support?',
      children: (
        <p className='text-grayZl-500'>
          Yes. Every book goes through professional formatting and cover design.
          We also provide editing and AI-powered writing tools to ensure your
          book is of the highest quality.
        </p>
      ),
    },
    {
      key: '8',
      label: 'Where will my book be available?',
      children: (
        <p className='text-grayZl-500'>
          Your book can be distributed across leading platforms, including
          Amazon, Flipkart, and Oranges by Zebralearn. We ensure maximum
          visibility through online distribution.
        </p>
      ),
    },
    {
      key: '9',
      label: 'Can businesses or brands publish with Oranges?',
      children: (
        <p className='text-grayZl-500'>
          Absolutely. We specialize in working with entrepreneurs, coaches, and
          business owners as well as brands who want to use their book as a
          brand-building tool. We help position your book as part of your larger
          business strategy.
        </p>
      ),
    },
    {
      key: '10',
      label: 'Do you work with global brands and authors?',
      children: (
        <p className='text-grayZl-500'>
          Yes, we are working with authors across the globe.
        </p>
      ),
    },
  ];

  const authors = [
    {
      title: 'Harjeet Khanduja',
      subTitle: 'Senior VP HR at Reliance Jio',
      image: author4,
    },
    {
      title: 'Bhuvana Subramanian',
      subTitle: 'CMO at Spinning Wheel',
      image: author1,
    },
    {
      title: 'Aseem Singhal ',
      subTitle: 'Founder at Unfluke',
      image: author2,
    },
    {
      title: 'Stan Peake',
      subTitle: 'Co-Founder at FSQ Consulting',
      image: author3,
    },
  ];

  const toggleAccordion = useCallback(
    (index: number) => {
      setActiveIndex(activeIndex === index ? -1 : index);
    },
    [activeIndex],
  );
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: false, // disable default arrows
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: 2.5,
          slidesToScroll: 1,
          dots: true,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2.1,
          slidesToScroll: 1,
          dots: true,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.5,
          slidesToScroll: 1,
          dots: true,
        },
      },
    ],
  };

  const companyImages = [
    {
      image: company1,
      link: 'https://drive.google.com/drive/folders/16dV_7wRgktRuC2vCu_wOtOOWb3PMhr3l?usp=sharing ',
    },
    {
      image: company2,
      link: 'https://www.amazon.in/zebra-learn-books/s?k=zebra+learn+books',
    },
    { image: company3, link: 'https://www.youtube.com/watch?v=ue_Wpvs2VlY' },
    { image: company4, link: '' },
    {
      image: company5,
      link: 'https://drive.google.com/drive/folders/1LXPJPaAz2fL5pMv-qVe4U0cWjsVy9Qgg',
    },
  ];

  const sliderHeaderClass = 'xl:text-5xl font-normal lg:mb-6';

  return (
    <>
      <Head>
        <title>
          Books Publisher in India for Finance, Marketing and more. | ZebraLearn
        </title>
        <meta
          name='description'
          content='Discover best books publisher in India specializing in non-fiction books. ZebraLearn offers cutting-edge design & technology insights, simplifying complex topics.
          '
          key='description'
        />
      </Head>
      <div className=' flex flex-col md:gap-14 gap-10'>
        <div className='flex md:flex-row flex-col justify-between items-center md:gap-10 gap-6 md:bg-hero-bg bg-no-repeat bg-center md:px-14 px-4 pt-auto pt-8'>
          <div className='flex flex-col gap-8'>
            <div className='flex flex-col md:gap-10 gap-6 md:items-start items-center'>
              <div className='flex flex-col md:w-auto w-full'>
                <p className='md:text-7xl text-[40px] md:leading-none leading-12 md:w-full w-auto'>
                  Publish Your Book.
                </p>
                <p className='md:text-7xl text-[40px] md:leading-none leading-12 md:w-full w-auto'>
                  Your Words.
                </p>
                <p className='md:text-7xl text-[40px] md:leading-none leading-12 md:w-full w-auto'>
                  Your Way.
                </p>
              </div>
              <Image
                className='w-[240px] h-auto md:hidden block'
                src='/hero-bg.png'
                width={240}
                height={240}
                alt='hero bg'
              />

              <div className='flex flex-col gap-6 items-start'>
                <div className='flex items-center justify-center gap-2'>
                  <BadgeCheckIcon className='text-success-400 text-2xl w-6 h-6 min-w-6 min-h-6' />
                  <p className='md:text-lg text-sm text-white'>
                    Published 12 Bestsellers so far
                  </p>
                </div>
                <div className='flex items-center justify-center gap-2'>
                  <BadgeCheckIcon className='text-success-400 text-2xl w-6 h-6 min-w-6 min-h-6' />
                  <p className='md:text-lg text-sm text-white'>
                    Premium Quality Publishing
                  </p>
                </div>
                <div className='flex items-center justify-center gap-2'>
                  <BadgeCheckIcon className='text-success-400 text-2xl w-6 h-6 min-w-6 min-h-6' />
                  <p className='md:text-lg text-sm text-white'>
                    Best in Class model to publish go-to books in the country.
                  </p>
                </div>
              </div>
            </div>
            <ButtonDefault
              onClick={() => setIsCallModalOpen(true)}
              roundedUnstyled
              className='flex md:hidden py-4 rounded-[4px]'
              size={0}
              variant={ButtonVariants.PRIMARY}
            >
              Request A Call Back
            </ButtonDefault>
          </div>
          <div className='w-[400px] md:flex hidden flex-col gap-4'>
            <CallBackForm
              onSuccess={() => showModal()}
              loading={loading}
              setLoading={setLoading}
            />
          </div>
        </div>
        <div className='flex flex-col gap-3 md:px-14 px-0'>
          <p className='text-white text-2xl text-center'>As Seen on</p>
          <div className='flex gap-4 items-center overflow-auto no-scrollbar'>
            {companyImages.map((i, index) => (
              <a target='_blank' key={index} href={i.link} className='flex-1'>
                <div
                  className={`
                  ${index === 0 ? 'md:ms-0 ms-4' : ''}
                  flex items-center justify-center border border-white/20 rounded-[4px] md:h-[132px] h-16 flex-1  min-w-[96px] hover:bg-white/5`}
                >
                  <Image
                    className='md:w-[96px] w-14'
                    width={96}
                    height={96}
                    alt='company image'
                    src={i.image}
                  />
                </div>
              </a>
            ))}
          </div>
        </div>
        <PaginatedBookSlider
          headingClass={sliderHeaderClass}
          categoryId={null}
          categoryName='Newly Launched Books'
          isHomePage
          order='asc'
          categoryIdFilter={categoryIdFilter}
          endDateFilter={nowMinus6Months}
        />
        <div className='flex flex-col gap-6 md:px-14 px-4'>
          <div className='flex items-center justify-between'>
            <p className={headerClass}>Authors at Zebralearn</p>
            {/* <div className='md:flex hidden gap-3 items-center'>
              <ButtonDefault
                onClick={() => sliderRef.current?.slickPrev()}
                variant={ButtonVariants.UNSTYLED}
                size={0}
                className='w-12 h-12 rounded-full bg-[#1c1c1c]'
              >
                <ChevronLeftIcon className=' w-6 h-6 text-white' />
              </ButtonDefault>
              <ButtonDefault
                onClick={() => sliderRef.current?.slickNext()}
                variant={ButtonVariants.UNSTYLED}
                size={0}
                className='w-12 h-12 rounded-full bg-[#1c1c1c]'
              >
                <ChevronRightIcon className=' w-6 h-6 text-white' />
              </ButtonDefault>
            </div> */}
          </div>
          <Slider {...settings} ref={sliderRef}>
            {authors.map((i, index) => (
              <div
                key={index}
                className={`
              ${index === 0 ? 'md:pr-3 pr-1.5' : index === authors.length - 1 ? 'md:pl-3 pl-1.5' : 'md:px-3 px-1.5'}

                h-full`}
              >
                <div className='rounded-[4px] flex flex-col pt-10 pb-8 bg-grayZl-950 h-full'>
                  <div className='relative mb-8 flex items-center justify-center '>
                    <Image src={i.image} alt='book' width={186} height={186} />
                    <div className='absolute top-0 left-0 w-full h-full shadow-[inset_0_-35px_22.2px_0_#121212] z-20'></div>
                  </div>
                  <div className='flex flex-col gap-3 px-10 '>
                    <p className='text-white md:text-2xl text-base text-center text-nowrap'>
                      {i.title}
                    </p>
                    <p className='text-grayZl-500 md:text-base text-xs text-center line-clamp-1 '>
                      {i.subTitle}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
          <div className='grid lg:grid-cols-4 md:grid-cols-2 sm:grid-cols-2 gap-6'></div>
        </div>
        <div className='flex flex-col gap-14  md:px-14 px-4'>
          <div className='flex flex-col gap-6'>
            <p className={headerClass}>Why Choose Us?</p>
            <SubTitle
              className='text-xl text-grayZl-500 text-start'
              title={
                <div>
                  <p>
                    Oranges focuses on making authors – self dependent and self
                    reliant and give them all the training to grow the book.
                    Lets be honest, no self- publisher takes on responsibility
                    of sales and marketing of the book. Their business model
                    does not allow.
                  </p>
                  <br />
                  <p>
                    At Oranges, we do not take that as well. But we make you –
                    the author, as capable as we can – to drive sales and drive
                    benefits to other businesses of yours through the book – In
                    all sense, become a true “Book-Prenuer
                  </p>
                </div>
              }
            />
          </div>

          <div className='relative w-full mx-auto bg-[#1A1A1A] border border-white/10 rounded-[4px] shadow-lg overflow-hidden'>
            {/* V/S Circle */}
            <div className='absolute left-1/2 top-16 -translate-x-1/2 -translate-y-1/2 md:w-20 md:h-20 w-9 h-9 rounded-full bg-black border border-white flex items-center justify-center text-white font-semibold md:text-[32px] text-xs z-[1]'>
              V/S
            </div>

            <div className='grid grid-cols-2   overflow-hidden '>
              {/* Headers */}
              <div className='bg-grayZl-950 border-r px-8 flex items-center justify-center text-center text-white md:text-[32px] text-lg  border-b border-white/10'>
                Other Publishers
              </div>
              <div className='md:py-[38px] py-6 px-8 text-center bg-[#23120B] text-white text-2xl font-semibold border-b border-white/10 flex flex-col items-center justify-center'>
                <LogoSvg width={131} className='md:h-auto h-9' />
              </div>

              {/* Comparison Rows */}
              {data.map((item, index) => (
                <div
                  key={index}
                  className={`grid grid-cols-subgrid col-span-2 ${index < data.length - 1 ? 'border-b border-white/10' : ''
                    }`}
                >
                  <div className='border-r border-white/10 bg-grayZl-950 md:p-6 p-3 text-grayZl-200 md:text-xl text-sm flex items-center justify-center text-center'>
                    {item.other}
                  </div>
                  <div className='p-6 bg-[#23120B] text-grayZl-200 md:text-xl text-sm flex items-center justify-center text-center'>
                    {item.orange}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <RequestCallBack
            onSuccess={() => showModal()}
            loading={loading}
            setLoading={setLoading}
          />
        </div>
        <PaginatedBookSlider
          categoryId={null}
          categoryName='Our Top 10 Selling Books'
          headingClass={sliderHeaderClass}
          isTopTen={true}
        />
        {/* <BooksPaginated />
        <BooksPaginated />
        <BooksPaginated />
        <BooksPaginated /> */}
        <div>
          <div className='flex md:flex-row flex-col items-center justify-center md:gap-16 gap-8 bg-[#151515] py-14 px-4'>
            <div className='w-[344px] flex items-center flex-col gap-6'>
              <div className='md:w-20 w-12 md:h-20 h-12 bg-white rounded-full flex items-center justify-center'>
                <HiOutlineBanknotes className='md:w-[40px] w-6 md:h-[40px] h-6 text-black' />
              </div>
              <div className='flex flex-col gap-3 '>
                <p className='text-center text-white md:text-2xl text-lg'>
                  Upto 90% Royalty
                </p>
                <p className='text-center text-grayZl-500 md:text-base text-sm font-light'>
                  Get Upto 90% Royalties on your titles
                </p>
              </div>
            </div>
            <div className='w-[344px] flex items-center flex-col gap-6'>
              <div className='md:w-20 w-12 md:h-20 h-12 bg-white rounded-full flex items-center justify-center'>
                <i className='bx  bxs-package text-black text-[40px] leading-0'></i>
                <BookEarthIcon className='md:w-[40px] w-6 md:h-[40px] h-6 text-black' />
              </div>
              <div className='flex flex-col gap-3 '>
                <p className='text-center text-white md:text-2xl text-lg'>
                  End to End Publishing
                </p>
                <p className='text-center text-grayZl-500 md:text-base text-sm'>
                  One stop solution for all your publishing, legal and marketing
                  needs for your book.
                </p>
              </div>
            </div>
            <div className='w-[344px] flex items-center flex-col gap-6'>
              <div className='md:w-20 w-12 md:h-20 h-12 bg-white rounded-full flex items-center justify-center'>
                <BiSolidPlaneAlt className='md:w-[40px] w-6 md:h-[40px] h-6 text-black' />
              </div>
              <div className='flex flex-col gap-3 '>
                <p className='text-center text-white md:text-2xl text-lg'>
                  Ships anywhere in the World
                </p>
                <p className='text-center text-grayZl-500 md:text-base text-sm'>
                  Cater to audience anywhere in the world.
                </p>
              </div>
            </div>
          </div>

          <div className='bg-orangeZl-600 py-14 flex md:flex-row flex-col justify-center overflow-hidden md:gap-0 gap-[72px]'>
            <div className='md:w-[560px] w-[368px] md:min-w-[560px] min-w-[368px] border-r border-white md:flex hidden flex-col md:gap-12 gap-8'>
              <div className='md:my-[122px] my-0 flex flex-col gap-5 pe-7 invisible'>
                <div className='flex gap-6'>
                  <div className='w-1.5 min-w-1.5 bg-white h-auto'></div>
                  <p className='md:text-[40px] md:leading-12 text-2xl'>
                    Publish Your Book in All Formats
                  </p>
                </div>
                <p className='md:text-lg text-sm text-white ms-7'>
                  Audiobook, Paperback, Hardcover and Digital
                </p>
              </div>
              <div className='w-full flex justify-center my-7'>
                <div className='md:w-[504px] w-[320px] md:h-[504px] h-[320px] rounded-full bg-black flex items-center justify-center '></div>
              </div>
            </div>
            <div className='md:w-[560px] w-full md:min-w-[560px] min-w-full md:border-r border-0 border-white flex md:flex-col flex-col-reverse md:gap-12 gap-8'>
              <div className='md:my-[122px] my-0 flex flex-col gap-5 pe-7'>
                <div className='flex gap-6'>
                  <div className='w-1.5 min-w-1.5 bg-white h-auto'></div>
                  <p className='md:text-[40px] md:leading-12 text-2xl'>
                    Publish Your Book in All Formats
                  </p>
                </div>
                <p className='md:text-lg text-sm text-white ms-7'>
                  Audiobook, Paperback, Hardcover and Digital
                </p>
              </div>
              <div className='w-full flex justify-center my-7'>
                <div className='md:w-[504px] w-[320px] md:h-[504px] h-[320px] rounded-full bg-black flex items-center justify-center grayscale'>
                  <Image
                    className='w-full h-full'
                    width={504}
                    height={504}
                    alt='feedback'
                    src={percentageHand}
                  />
                </div>
              </div>
            </div>
            <div className='md:w-[560px] w-full md:min-w-[560px] min-w-full md:border-r border-0 border-white flex flex-col md:gap-12 gap-8'>
              <div className='w-full flex justify-center my-7'>
                <div className='md:w-[504px] w-[320px] md:h-[504px] h-[320px] rounded-full bg-black flex items-center justify-center '>
                  <Image
                    className='w-full h-full grayscale'
                    width={504}
                    height={504}
                    alt='feedback'
                    src={internetImage}
                  />
                </div>
              </div>
              <div className='md:my-[122px] my-0 flex flex-col gap-5 pe-7'>
                <div className='flex gap-6'>
                  <div className='w-1.5 min-w-1.5 bg-white h-auto'></div>
                  <p className='md:text-[40px] md:leading-12 text-2xl text-white'>
                    Publish high quality content that readers love
                  </p>
                </div>
                <p className='md:text-lg text-sm text-white ms-7'>
                  Become Completely Competent at selling and driving maximum
                  value from your book.
                </p>
              </div>
            </div>
            <div className='md:w-[560px] w-[368px] md:min-w-[560px] min-w-[368px] border-r border-white md:flex hidden flex-col md:gap-12 gap-8'>
              <div className='w-full flex justify-center my-7'>
                <div className='md:w-[504px] w-[320px] md:h-[504px] h-[320px] rounded-full bg-black flex items-center justify-center '></div>
              </div>
              <div className='md:my-[122px] my-0 flex flex-col gap-5 pe-7 invisible'>
                <div className='flex gap-6'>
                  <div className='w-1.5 min-w-1.5 bg-white h-auto'></div>
                  <p className='md:text-[40px] md:leading-12 text-2xl'>
                    Publish Your Book in All Formats — Audiobook, Paperback,
                    Hardcover & Digital
                  </p>
                </div>
                <p className='md:text-lg text-sm text-white ms-7'>
                  Share your story in print and eBook versions made easy, all
                  from one platform.
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* <div className=' flex flex-col gap-10 md:px-14 px-4'>
          <div className='flex flex-col gap-6'>
            <p className='md:text-2xl text-sm text-grayZl-600 text-center'>
              Join an amazing community of authors creating history
            </p>
            <p className='md:text-5xl text-2xl text-white text-center'>
              Our 20+ authors have sold books worth over INR 30+ crores
            </p>
          </div>

          <Slider {...settingsTestimonials} ref={sliderTestimonialsRef}>
            {testimonials.map((i, index) => (
              <div key={index} className='px-2'>
                <div
                  key={index}
                  className='bg-white/10 py-10 px-6  rounded-[4px] flex flex-col gap-8 w-full'
                >
                  <QuoteIcon2 />
                  <p className='text-lg text-white'>{i.review}</p>
                  <div className='flex items-center gap-3'>
                    <Avatar className='w-12 h-12' src='/logo.png' />
                    <div className='flex flex-col'>
                      <p className='text-white text-base'>Maryann Thompson</p>
                      <p className='text-sm text-grayZl-500'>
                        Lead Accountability Supervisor
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
          <RequestCallBack
            onSuccess={() => showModal()}
            loading={loading}
            setLoading={setLoading}
          />
        </div> */}
        <div className=' flex flex-col items-center gap-10 md:px-0 px-4'>
          <div className='flex flex-col gap-2'>
            <p className='text-white md:text-5xl text-2xl text-center'>
              Frequently Asked Questions
            </p>
            <p className='text-white md:text-lg text-sm text-center'>
              Can’t find what you are looking for?
            </p>
          </div>
          <div className='w-full max-w-[800px]'>
            <div className='flex flex-col gap-3 w-full'>
              {items.map((item, index) => (
                <motion.div
                  key={index}
                  className={clsx(
                    activeIndex === index ? 'bg-[#282829]' : 'bg-[#1C1C1C]',
                    'rounded-[8px] sm:rounded-[10px] overflow-hidden',
                  )}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                >
                  <motion.button
                    className='w-full flex items-center justify-between px-3 sm:px-4 md:px-4 lg:px-6 py-3 sm:py-3 md:py-3 lg:py-5 text-left text-white gap-5'
                    onClick={() => toggleAccordion(index)}
                  // whileHover={{
                  //   backgroundColor: 'rgba(40, 40, 41, 0.5)',
                  // }}
                  >
                    <span className='text-sm md:text-base/6 md:font-semibold font-medium'>
                      {item.label}
                    </span>
                    <motion.div
                      // animate={{
                      //   rotate: activeIndex === index ? 45 : 0,
                      // }}
                      transition={{ duration: 0.2 }}
                    >
                      {activeIndex === index ? (
                        <MinusIcon className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-4 text-white' />
                      ) : (
                        <PlusIcon className='h-3 w-3 sm:h-4 sm:w-4 md:h-5 md:w-4 text-[#FFFFFF99]' />
                      )}
                    </motion.div>
                  </motion.button>

                  <AnimatePresence>
                    {activeIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.3,
                          ease: 'easeInOut',
                        }}
                        className='overflow-hidden'
                      >
                        <div className='px-4 sm:px-4 md:px-4 lg:px-6 py-3 sm:pb-3 md:pb-3 lg:pb-5 pt-0 text-sm md:text-xs lg:text-sm/6 font-normal text-grayZl-500'>
                          {item.children}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
          <RequestCallBack
            onSuccess={() => showModal()}
            loading={loading}
            setLoading={setLoading}
          />
        </div>
        <CallBackSuccessModal
          open={isModalOpen}
          onCancel={handleCancel}
          onOk={handleOk}
        />
        <CallBackFormModal
          onSuccess={() => {
            showModal();
            setIsCallModalOpen(false);
          }}
          open={isCallModalOpen}
          loading={loading}
          setLoading={setLoading}
          onCancel={() => setIsCallModalOpen(false)}
          onOk={() => setIsCallModalOpen(false)}
        />
      </div>
    </>
  );
};

export default Home;
